import React, { useEffect } from 'react'
import getConfig from 'next/config'
import { FormattedMessage } from 'react-intl'
import messages from '../messages'
import { Linq } from '@src/components/Linq'
import { Typography } from '@material-ui/core'
import { OrderItemTrackingInfo, CarrierId } from '@src/types/graphql-types'
import { sendOrderCenterEvent } from '@src/routes/OrderCenter/analytics'
import { orderCenterRoutes, useRouter } from '@src/routes'
import { useGetShipmentTrackingInfoLazyQuery } from '@src/queries/GetShipmentTrackingInfoQuery.generated'

interface TrackingNumberProps {
  tracking: OrderItemTrackingInfo
  materialNumber: string
  orderNumber?: string | null
  shipToZip?: string | null
  classes?: { trackingLink?: string }
}

export const getCarrierId = (trackingUrl: string): CarrierId | '' => {
  if (trackingUrl?.includes('fedex')) {
    return CarrierId.Fedex
  } else if (trackingUrl?.includes('dpd')) {
    return CarrierId.Dpd
  } else {
    return ''
  }
}

export const LinkToItemShipmentTracking: React.FC<TrackingNumberProps> = ({
  tracking,
  materialNumber,
  orderNumber,
  shipToZip,
  classes = {},
}) => {
  const router = useRouter()

  // Testing ability of backend data for tracking links is poor in lower envs. Need to have quick shutoff for tracking links in prod if something goes wrong.
  const {
    publicRuntimeConfig: { featureFlags },
  } = getConfig()
  const mTrackLinksEnabled = featureFlags?.mTrackLinking

  const hasNoTrackingNumber =
    !tracking.trackingNumber || tracking.trackingNumber === 'NOT_PROVIDED'

  const hasNoTrackingUrl =
    !tracking.trackingUrl || tracking.trackingUrl === 'NOT_PROVIDED'

  const shippingZipCode = shipToZip || router.query.postalCode

  const carrierId = getCarrierId(tracking.trackingUrl)

  const giveInternalTrackingPage =
    mTrackLinksEnabled && orderNumber && shippingZipCode && carrierId

  const customTrackingUrl = giveInternalTrackingPage
    ? orderCenterRoutes.orderTracking(
        orderNumber,
        tracking.trackingNumber,
        shippingZipCode,
        carrierId
      )
    : tracking.trackingUrl

  const [getShipmentTrackingInfo, { data }] =
    useGetShipmentTrackingInfoLazyQuery()

  useEffect(() => {
    if (carrierId && tracking.trackingNumber) {
      getShipmentTrackingInfo({
        variables: {
          trackingNumber: tracking.trackingNumber,
          carrierId: carrierId,
        },
      })
    }
  }, [carrierId, tracking.trackingNumber, getShipmentTrackingInfo])

  const { deliveryDate } = data?.getShipmentTrackingInfo || {}

  // Default state is if there is no tracking number give a not provided statement. If it is use the number given as plain text.
  let trackingNumberElement = hasNoTrackingNumber ? (
    <FormattedMessage {...messages.TRACK_SHIPMENT_NOT_PROVIDED} />
  ) : (
    tracking.trackingNumber
  )

  // In the event there is a tracking URL go ahead and build the anchor element.
  if (!hasNoTrackingUrl) {
    trackingNumberElement = (
      <Linq
        href={customTrackingUrl}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-scrub
        onClick={() => {
          sendOrderCenterEvent(
            'open item tracking',
            `${materialNumber} > ${
              tracking.trackingNumber || 'tracking_link_without_id'
            }`
          )
        }}
      >
        {hasNoTrackingNumber ? (
          <FormattedMessage {...messages.TRACK_SHIPMENT} />
        ) : deliveryDate ? (
          <FormattedMessage id="DELIVERED" defaultMessage="Delivered" />
        ) : (
          tracking.trackingNumber
        )}
      </Linq>
    )
  }

  return (
    <div key={tracking.trackingNumber} className={classes.trackingLink}>
      {!deliveryDate && hasNoTrackingUrl && (
        <Typography component="span" variant="body2">
          {trackingNumberElement}
        </Typography>
      )}

      {!hasNoTrackingUrl && trackingNumberElement}
    </div>
  )
}

export default LinkToItemShipmentTracking
